import { useEffect, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { FormProvider } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Info, Loader2, Plus, X } from 'lucide-react';
import { CustomFieldDefinition, ProjectType } from '@/types';
import { CustomFieldEntity, CustomFieldTypes } from '@/types/enums';
import { useCreateCustomField } from '@/hooks/api/useCustomFields';

// ─── Types ────────────────────────────────────────────────────────────────────

interface CreateAttributeFormProps {
  selectedEntity?: CustomFieldEntity;
  selectedProject?: ProjectType | null;
  onSuccess?: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const CreateAttributeForm = ({
  selectedEntity,
  selectedProject,
  onSuccess,
}: CreateAttributeFormProps) => {
  const [selectOptions, setSelectOptions] = useState<string[]>(['']);

  const methods = useForm<CustomFieldDefinition>({
    defaultValues: {
      fieldType: CustomFieldTypes.TEXT,
      isRequired: false,
      isVisible: true,
      isEditable: true,
    },
  });

  const {
    setValue,
    handleSubmit,
    register,
    watch,
    reset,
    formState: { isSubmitting },
  } = methods;

  const fieldType = watch('fieldType');

  const { createCustomField, isCreating } = useCreateCustomField();

  const onSubmit: SubmitHandler<CustomFieldDefinition> = (formData) => {
    if (!formData.fieldLabel) return;

    createCustomField(
      {
        entity: selectedEntity ?? 'TICKET',
        projectTypeId: selectedProject?.id,
        fieldName: formData.fieldName,
        fieldLabel: formData.fieldLabel,
        fieldType: formData.fieldType ?? CustomFieldTypes.TEXT,
        isRequired: formData.isRequired,
        isEditable: formData.isEditable,
        isVisible: formData.isVisible,
        fieldOptions:
          fieldType === CustomFieldTypes.SELECT
            ? selectOptions.filter(Boolean)
            : [],
      },
      {
        onSuccess: () => {
          reset();
          setSelectOptions(['']);
          onSuccess?.();
        },
      },
    );
  };

  // ── Select option helpers ──────────────────────────────────────────────────

  const addSelectOption = () => setSelectOptions((prev) => [...prev, '']);

  const updateSelectOption = (index: number, value: string) =>
    setSelectOptions((prev) =>
      prev.map((opt, i) => (i === index ? value : opt)),
    );

  const removeSelectOption = (index: number) =>
    setSelectOptions((prev) => prev.filter((_, i) => i !== index));

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* Field name + label */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="fieldName">Field Name</Label>
            <Input
              id="fieldName"
              {...register('fieldName', { required: true })}
              placeholder="e.g., firstName"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="fieldLabel">Field Label</Label>
            <Input
              id="fieldLabel"
              {...register('fieldLabel', { required: true })}
              placeholder="e.g., First Name"
            />
          </div>
        </div>

        {/* Field type */}
        <div className="space-y-1.5">
          <Label htmlFor="fieldType">Field Type</Label>
          <Select
            defaultValue={CustomFieldTypes.TEXT}
            onValueChange={(value: CustomFieldTypes) =>
              setValue('fieldType', value)
            }
          >
            <SelectTrigger id="fieldType">
              <SelectValue placeholder="Select field type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={CustomFieldTypes.TEXT}>Text</SelectItem>
              <SelectItem value={CustomFieldTypes.SELECT}>
                Select (Dropdown)
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Description */}
        <div className="space-y-1.5">
          <Label htmlFor="description">
            Description{' '}
            <span className="text-muted-foreground font-normal">
              (optional)
            </span>
          </Label>
          <Textarea
            id="description"
            {...register('description')}
            placeholder="Field description or placeholder text"
            className="min-h-[72px] resize-none"
          />
        </div>

        {/* Select options — only shown when type is SELECT */}
        {fieldType === CustomFieldTypes.SELECT && (
          <div className="space-y-2">
            <Label>Options</Label>
            <div className="space-y-2">
              {selectOptions.map((option, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={option}
                    onChange={(e) => updateSelectOption(index, e.target.value)}
                    placeholder={`Option ${index + 1}`}
                  />
                  {selectOptions.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeSelectOption(index)}
                      className="shrink-0 text-muted-foreground hover:text-destructive"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addSelectOption}
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Option
              </Button>
            </div>
          </div>
        )}

        {/* Flags */}
        <div className="space-y-3 rounded-lg border border-border bg-muted/20 p-3">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Options
          </p>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isRequired"
              {...register('isRequired')}
              className="accent-primary"
            />
            <Label htmlFor="isRequired" className="cursor-pointer">
              Required
            </Label>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isEditable"
              {...register('isEditable')}
              className="accent-primary"
            />
            <Label htmlFor="isEditable" className="cursor-pointer">
              Editable
            </Label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info
                    size={14}
                    className="text-muted-foreground cursor-help"
                  />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">
                    Whether this field can be updated after creation
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>

        {/* Submit */}
        <Button
          type="submit"
          className="w-full"
          disabled={isCreating || isSubmitting}
        >
          {isCreating ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating...
            </>
          ) : (
            <>
              <Plus className="h-4 w-4 mr-2" /> Add Field
            </>
          )}
        </Button>
      </form>
    </FormProvider>
  );
};
